import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCarousel } from "../hooks/useCarousel";

const FALLBACKS = {
  Villa:      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=700&q=80",
  Studio:     "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=700&q=80",
  Commercial: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=700&q=80",
  Apartment:  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=700&q=80",
};
const FALLBACK_DEFAULT = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=700&q=80";

/** Responsive per-view count based on current viewport width. */
function getPerView() {
  if (typeof window === "undefined") return 4;
  if (window.innerWidth < 600)  return 1;
  if (window.innerWidth < 960)  return 2;
  return 4;
}

/**
 * Auto-advancing infinite carousel of featured property cards.
 *
 * @param {Object}  props
 * @param {Array}   props.properties - Array of property objects to display.
 */
export default function FeaturedCarousel({ properties }) {
  const { t } = useTranslation("common");
  const [perView, setPerView] = useState(getPerView);

  useEffect(() => {
    const onResize = () => setPerView(getPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const total = properties.length;

  const {
    cur,
    trackOffset,
    paused,
    cloneOffset: _cloneOffset,
    getRealIndex: _getRealIndex,
    handleNext,
    handlePrev,
    handleGoTo,
    buildClones,
  } = useCarousel(total, perView);

  /** Triple-cloned list — memoised to avoid rebuilding every render. */
  const cloned = useMemo(() => buildClones(properties), [buildClones, properties]);

  return (
    <div className="fc-wrap">
      <div className="fc-progress">
        <div
          className="fc-bar"
          key={`${cur}-${paused}`}
          style={paused ? { animationPlayState: "paused" } : {}}
        />
      </div>

      <div className="fc-outer">
        <div
          className="fc-track"
          style={{
            transform:  `translateX(${trackOffset}%)`,
            transition: "transform .7s cubic-bezier(.77,0,.175,1)",
          }}
        >
          {cloned.map((property, i) => {
            const realIdx       = i % total;
            const inMiddleBlock = i >= total && i < total * 2;
            const isActive      = realIdx === cur && inMiddleBlock;
            const photo         = (property.images?.[0]) || FALLBACKS[property.type] || FALLBACK_DEFAULT;

            return (
              <Link
                key={`${property.id}-${i}`}
                to={`/property/${property.slug}`}
                className={`fc-card ${isActive ? "fc-card-active" : ""}`}
                style={{ width: `${100 / perView}%`, flexShrink: 0 }}
                tabIndex={inMiddleBlock ? 0 : -1}
                aria-hidden={!inMiddleBlock}
                onClick={(e) => {
                  if (!inMiddleBlock) { e.preventDefault(); return; }
                  handleGoTo(realIdx);
                }}
              >
                <div className="fc-card-img">
                  <img
                    src={photo}
                    alt={property.title}
                    loading="lazy"
                    onError={(e) => { e.target.src = FALLBACKS[property.type] || FALLBACK_DEFAULT; }}
                  />
                  <span className={`card-badge ${property.priceType === "rent" ? "badge-rent" : "badge-sale"}`}>
                    {t(property.priceType === "rent" ? "badge.rent" : "badge.sale")}
                  </span>
                </div>
                <div className="card-body">
                  <div className="card-price">
                    {new Intl.NumberFormat("en-AE").format(property.price)}{" "}
                    <span className="card-currency">
                      AED{property.priceType === "rent" ? "/yr" : ""}
                    </span>
                  </div>
                  <div className="card-title">{property.title}</div>
                  <div className="card-loc">{property.location}</div>
                  <div className="card-meta">
                    <span>{property.beds > 0 ? `${property.beds} bed` : "Studio"}</span>
                    <span>{property.baths} bath</span>
                    <span>{property.sqft.toLocaleString()} ft²</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="fc-controls">
        <button className="fc-arrow" onClick={handlePrev} aria-label="Previous property">←</button>
        <div className="fc-dots">
          {properties.map((_, i) => (
            <button
              key={i}
              className={`fc-dot ${i === cur ? "on" : ""}`}
              onClick={() => handleGoTo(i)}
              aria-label={`Go to property ${i + 1}`}
            />
          ))}
        </div>
        <button className="fc-arrow" onClick={handleNext} aria-label="Next property">→</button>
      </div>
    </div>
  );
}
